import type { BaseApi } from '@frontend/api-client/utils/types';
import type {
  AcknowledgeClientPromptRequest,
  AcknowledgeClientPromptResponse,
  ChangeEmailRequest,
  ChangeEmailResponse,
  ClerkSSOLoginRequest,
  ClerkSSOLoginResponse,
  Client,
  ClientStatus,
  CreateClientRequest,
  CreateClientResponse,
  CreateMCPSocialAuthRequest,
  CreateMCPSocialAuthResponse,
  DeleteClientRequest,
  EditProfileRequest,
  FrictionlessSignupRequest,
  FrictionlessSSOSignupRequest,
  LoginRequest,
  LoginResponse,
  ReengagementActivateResponse,
  RetrieveClientPromptsResponse,
  RetrieveClientResponse,
  RetrieveClientStatusResponse,
  RetrieveMCPSocialAuthRequest,
  RetrieveMCPSocialAuthResponse,
  RetrieveReferrerDetailsResponse,
  UpdatePlanRequest,
} from '@frontend/api-types';
import { convertArrayObjectKeysCase, convertObjectKeysCase, isNil, makeCent } from '@frontend/duck-tape';
import { createTag } from '../../utils/tags';
import { CLIENT_ENDPOINTS } from './endpoints';
import type { MembershipPlanShape, RetrievePlanTiersArg } from './utils';

export const getClientApi = (baseApi: BaseApi) => {
  const clientApi = baseApi.injectEndpoints({
    endpoints: (builder) => ({
      acknowledgeClientPrompt: builder.mutation<void, AcknowledgeClientPromptRequest>({
        invalidatesTags: [createTag({ id: 'LIST', type: 'ClientPrompt' })],
        query: (payload) => ({
          body: convertObjectKeysCase(payload, 'snakeCase'),
          method: 'POST',
          transformResponse: (response: GenericRecord[]) =>
            convertArrayObjectKeysCase(response, 'camelCase') as AcknowledgeClientPromptResponse,
          url: CLIENT_ENDPOINTS.updateClientPrompt(),
        }),
      }),
      changeEmail: builder.mutation<ChangeEmailResponse, ChangeEmailRequest>({
        query: (payload) => ({
          body: convertObjectKeysCase(payload, 'snakeCase'),
          method: 'PATCH',
          url: CLIENT_ENDPOINTS.changeEmail(),
        }),
      }),
      clerkSSOLogin: builder.mutation<ClerkSSOLoginResponse, ClerkSSOLoginRequest>({
        onQueryStarted: async (_args, { dispatch, queryFulfilled }) => {
          try {
            const { data } = await queryFulfilled;
            dispatch(clientApi.util.updateQueryData('retrieveClient', undefined, () => data.user));
          } catch {
            // eslint-disable-next-line no-console
            console.error('Failed to retrieve client after SSO login');
          }
        },
        query: (payload) => ({
          body: convertObjectKeysCase(payload, 'snakeCase'),
          method: 'POST',
          url: CLIENT_ENDPOINTS.clerkSSOLogin(),
        }),
      }),
      createClient: builder.mutation<CreateClientResponse, CreateClientRequest>({
        query: (payload) => ({
          body: convertObjectKeysCase(payload, 'snakeCase'),
          method: 'POST',
          url: CLIENT_ENDPOINTS.createClient(),
        }),
      }),
      createMCPSocialAuth: builder.mutation<CreateMCPSocialAuthResponse, CreateMCPSocialAuthRequest>({
        onQueryStarted: async (_args, { dispatch, queryFulfilled }) => {
          try {
            const { data } = await queryFulfilled;
            dispatch(clientApi.util.updateQueryData('retrieveClient', undefined, () => data.user));
          } catch {
            // eslint-disable-next-line no-console
            console.error('Failed to retrieve client after MCP social auth');
          }
        },
        query: (payload) => ({
          body: convertObjectKeysCase(payload, 'snakeCase'),
          method: 'POST',
          url: CLIENT_ENDPOINTS.createMCPSocialAuth(),
        }),
        transformResponse: (response: GenericRecord) =>
          convertObjectKeysCase(response, 'camelCase') as CreateMCPSocialAuthResponse,
      }),
      deleteClient: builder.mutation<void, DeleteClientRequest>({
        query: ({ clientId }) => ({
          method: 'DELETE',
          url: CLIENT_ENDPOINTS.deleteClient(clientId),
        }),
      }),
      editProfile: builder.mutation<void, EditProfileRequest>({
        invalidatesTags: [createTag('Client')],
        onQueryStarted: async (payload, { dispatch, queryFulfilled }) => {
          const patchResult = dispatch(
            clientApi.util.updateQueryData('retrieveClient', undefined, (cachedClient) => {
              const optimisticUpdate: Partial<Pick<Client, 'birthday' | 'firstName' | 'lastName' | 'phoneNumber'>> = {
                ...(typeof payload.birthday !== 'undefined'
                  ? { birthday: payload.birthday as Client['birthday'] }
                  : {}),
                ...(typeof payload.firstName !== 'undefined' ? { firstName: payload.firstName } : {}),
                ...(typeof payload.lastName !== 'undefined' ? { lastName: payload.lastName } : {}),
                ...(typeof payload.phoneNumber !== 'undefined' ? { phoneNumber: payload.phoneNumber } : {}),
              };

              return {
                ...cachedClient,
                ...optimisticUpdate,
              };
            }),
          );

          try {
            await queryFulfilled;
          } catch {
            patchResult.undo();
          }
        },
        query: (payload) => ({
          body: convertObjectKeysCase(payload, 'snakeCase'),
          method: 'PATCH',
          url: CLIENT_ENDPOINTS.editProfile(),
        }),
      }),
      frictionlessSSOSignup: builder.mutation<ClerkSSOLoginResponse, FrictionlessSSOSignupRequest>({
        onQueryStarted: async (_args, { dispatch, queryFulfilled }) => {
          try {
            const { data } = await queryFulfilled;
            dispatch(clientApi.util.updateQueryData('retrieveClient', undefined, () => data.user));
          } catch {
            // eslint-disable-next-line no-console
            console.error('Failed to retrieve client after frictionless SSO signup');
          }
        },
        query: ({ clerkToken, taskDescription }) => ({
          body: convertObjectKeysCase({ clerkToken }, 'snakeCase'),
          method: 'POST',
          url: CLIENT_ENDPOINTS.frictionlessSSOSignup(taskDescription),
        }),
      }),
      frictionlessSignup: builder.mutation<ClerkSSOLoginResponse, FrictionlessSignupRequest>({
        onQueryStarted: async (_args, { dispatch, queryFulfilled }) => {
          try {
            const { data } = await queryFulfilled;
            dispatch(clientApi.util.updateQueryData('retrieveClient', undefined, () => data.user));
          } catch {
            // eslint-disable-next-line no-console
            console.error('Failed to retrieve client after frictionless signup');
          }
        },
        query: (payload) => ({
          body: convertObjectKeysCase(payload, 'snakeCase'),
          method: 'POST',
          url: CLIENT_ENDPOINTS.frictionlessSignup(),
        }),
      }),
      login: builder.mutation<LoginResponse, LoginRequest>({
        onQueryStarted: async (_args, { dispatch, queryFulfilled }) => {
          try {
            const { data } = await queryFulfilled;
            dispatch(clientApi.util.updateQueryData('retrieveClient', undefined, () => data.user));
          } catch {
            /* If there is an error, don't update the cache */
          }
        },
        query: (payload) => ({
          body: convertObjectKeysCase(payload, 'snakeCase'),
          method: 'POST',
          url: CLIENT_ENDPOINTS.login(),
        }),
      }),
      reengagementActivate: builder.mutation<ReengagementActivateResponse, void>({
        invalidatesTags: [createTag('Client')],
        query: () => ({
          method: 'POST',
          url: CLIENT_ENDPOINTS.reengagementActivate(),
        }),
        transformResponse: (response: GenericRecord) =>
          convertObjectKeysCase(response, 'camelCase') as ReengagementActivateResponse,
      }),
      retrieveClient: builder.query<RetrieveClientResponse, void>({
        providesTags: [createTag('Client')],
        query: CLIENT_ENDPOINTS.retrieveClient,
        transformResponse: ({ user }) => convertObjectKeysCase(user, 'camelCase') as Client,
      }),
      retrieveClientPrompts: builder.query<RetrieveClientPromptsResponse, void>({
        providesTags: [createTag({ id: 'LIST', type: 'ClientPrompt' })],
        query: CLIENT_ENDPOINTS.retrieveClientPrompts,
        transformResponse: (response: GenericRecord[]) =>
          convertArrayObjectKeysCase(response, 'camelCase') as RetrieveClientPromptsResponse,
      }),
      retrieveClientStatus: builder.query<RetrieveClientStatusResponse, void>({
        providesTags: [createTag('Client')],
        query: CLIENT_ENDPOINTS.retrieveClientStatus,
        transformResponse: (response: GenericRecord) => convertObjectKeysCase(response, 'camelCase') as ClientStatus,
      }),
      retrieveMCPSocialAuth: builder.query<RetrieveMCPSocialAuthResponse, RetrieveMCPSocialAuthRequest>({
        query: ({ clerkToken }) => ({
          url: CLIENT_ENDPOINTS.retrieveMCPSocialAuth(clerkToken),
        }),
      }),
      retrievePlanTiers: builder.query<(MembershipPlanShape & { isSelectable: boolean })[], RetrievePlanTiersArg>({
        query: CLIENT_ENDPOINTS.retrievePlanTiers,
        transformResponse: (response: GenericRecord[]) => {
          const plans = convertArrayObjectKeysCase(response, 'camelCase') as (MembershipPlanShape & {
            isSelectable: boolean;
          })[];
          return plans.map((plan) => ({
            ...plan,
            discountDurationMonths: plan.discountDurationMonths ?? undefined,
            discountPrice: isNil(plan.discountPrice) ? undefined : makeCent(plan.discountPrice),
            price: makeCent(plan.price),
          }));
        },
      }),
      retrievePlans: builder.query<
        {
          code: string;
          maxAccounts: number;
          title: string;
        }[],
        void
      >({
        query: CLIENT_ENDPOINTS.retrievePlans,
        transformResponse: (response: GenericRecord[]) =>
          convertArrayObjectKeysCase(response, 'camelCase') as {
            code: string;
            maxAccounts: number;
            title: string;
          }[],
      }),
      retrieveReferrerDetails: builder.query<RetrieveReferrerDetailsResponse, string>({
        query: CLIENT_ENDPOINTS.retrieveReferrerDetails,
        transformResponse: (response: GenericRecord) =>
          convertObjectKeysCase(response, 'camelCase') as RetrieveReferrerDetailsResponse,
      }),
      updatePlan: builder.mutation<void, UpdatePlanRequest>({
        invalidatesTags: [createTag('Client'), createTag('Credit'), createTag('Household')],
        query: (payload) => ({
          body: convertObjectKeysCase(payload, 'snakeCase'),
          method: 'PATCH',
          url: CLIENT_ENDPOINTS.updatePlan(),
        }),
      }),
    }),
    overrideExisting: true,
  });

  return clientApi;
};
